/**
 * Badge affichant le statut de paiement auto-calculé d'un chantier
 */

import { Badge } from '@/components/ui/badge';
import { Check, Clock, AlertCircle, AlertTriangle, Ban, FileText, Wallet } from 'lucide-react';
import type { StatutPaiementChantier } from '@/types/paiement';
import { LABELS_STATUT_PAIEMENT_CHANTIER } from '@/types/paiement';


interface StatutPaiementBadgeProps {
  statut: StatutPaiementChantier | null | undefined;
  className?: string;
  showIcon?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Configuration des couleurs et icônes par statut
 */
const STATUT_CONFIG: Record<StatutPaiementChantier, {
  variant: 'default' | 'secondary' | 'destructive' | 'outline';
  bgClass: string;
  textClass: string;
  icon: React.ComponentType<{ className?: string }>;
}> = {
  non_facture: {
    variant: 'secondary',
    bgClass: 'bg-gray-500/10',
    textClass: 'text-gray-400',
    icon: FileText,
  },
  en_attente: {
    variant: 'outline',
    bgClass: 'bg-yellow-500/10',
    textClass: 'text-yellow-500',
    icon: Clock,
  },
  acompte_recu: {
    variant: 'outline',
    bgClass: 'bg-blue-500/10',
    textClass: 'text-blue-500',
    icon: Wallet,
  },
  paye_partiellement: {
    variant: 'outline',
    bgClass: 'bg-orange-500/10',
    textClass: 'text-orange-500',
    icon: AlertTriangle,
  },
  paye_integralement: {
    variant: 'default',
    bgClass: 'bg-green-500/20',
    textClass: 'text-green-500',
    icon: Check,
  },
  en_retard: {
    variant: 'destructive',
    bgClass: 'bg-red-500/10',
    textClass: 'text-red-500',
    icon: AlertCircle,
  },
  impaye: {
    variant: 'destructive',
    bgClass: 'bg-red-500/20',
    textClass: 'text-red-600',
    icon: Ban,
  },
};


export function StatutPaiementBadge({
  statut,
  className = '',
  showIcon = true,
  size = 'md'
}: StatutPaiementBadgeProps) {
  // Statut par défaut si null/undefined
  const effectiveStatut = statut || 'non_facture';
  const config = STATUT_CONFIG[effectiveStatut];
  const label = LABELS_STATUT_PAIEMENT_CHANTIER[effectiveStatut];
  const Icon = config.icon;

  // Classes de taille
  const sizeClasses = {
    sm: 'text-xs px-2 py-0.5',
    md: 'text-sm px-3 py-1',
    lg: 'text-base px-4 py-1.5',
  };

  const iconSizes = {
    sm: 'w-3 h-3',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
  };

  return (
    <Badge
      variant={config.variant}
      className={`
        ${config.bgClass}
        ${config.textClass}
        ${sizeClasses[size]}
        font-medium
        flex items-center gap-1.5
        border-0
        ${className}
      `}
    >
      {showIcon && <Icon className={iconSizes[size]} />}
      {label}
    </Badge>
  );
}

export default StatutPaiementBadge;
